import { styled } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import React from 'react'

import { MESSAGE_TYPES } from '@/app/module/campaigns/definitions'
import { LocalAction, LocalActionType, Part } from '@/app/module/campaigns/types'
import { getPartContentPreview } from '@/app/module/campaigns/utils/part-content'
import type { FilesType, PersonalizationType } from '@/app/types'

import { useCampaignParts } from '../hooks/use-campaign-parts'

type Props = {
  action: LocalAction
  parts: Part[]
  files?: FilesType
  personalizationList?: PersonalizationType
  onEdit?: () => void
}

const FinalActionPreview: React.FC<Props> = ({ action, parts, onEdit }) => {
  const preview = getPreviewText(action, parts)
  if (!preview) {
    return null
  }

  return (
    <Container limitWidth={action.type === LocalActionType.Jump} onClick={onEdit}>
      <Typography className="campaign-preview-text" variant="body2" color="textSecondary" noWrap sx={{ minWidth: 0 }}>
        {preview}
      </Typography>
    </Container>
  )
}

const getPreviewText = (action: LocalAction, parts: Part[]): string => {
  switch (action.type) {
    case LocalActionType.Pickup:
      return `Campaign ${action.pickUp.campId}`
    case LocalActionType.Jump:
      return buildJumpPreview(action, parts)
    case LocalActionType.FinishFlow:
      return 'End campaign for contact'
    case LocalActionType.Hangup:
      return 'Hangup'
    default:
      return ''
  }
}

const buildJumpPreview = (action: LocalAction, parts: Part[]): string => {
  const partId = (action as any)?.jump?.nextPart
  if (!partId) return 'Jump to question'

  const idx = parts.findIndex((p) => p.id === partId)
  if (idx === -1) return 'Jump to question'

  const part = parts[idx]
  const messageType = MESSAGE_TYPES.find((t) => Object.hasOwn(part, t.key))
  if (part.label) {
    return `${idx + 1}. ${part.label}`
  }
  if (messageType) {
    const text = getPartContentPreview(part)
    return `${idx + 1}. ${messageType.label}: ${text}`
  }
  return `Question ${idx + 1}`
}

const Container = styled('div', {
  shouldForwardProp: (prop) => prop !== 'limitWidth',
})<{ limitWidth: boolean }>(({ limitWidth }) => ({
  display: 'flex',
  alignItems: 'center',
  flex: '0 1 auto',
  maxWidth: limitWidth ? 360 : 'none',
  minWidth: 0,
  overflow: 'hidden',
  cursor: 'pointer',

  '& .campaign-preview-text': {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },

  '& .MuiIcon-root': {
    fontSize: 18,
  },
}))

export default (props: Omit<Props, 'parts'>) => {
  const parts = useCampaignParts()

  return <FinalActionPreview {...props} parts={parts} />
}
